import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown } from "lucide-react";
import AuthShell from "./AuthShell";
import { useAuth } from "@/lib/auth";
import { BRAND } from "@/branding.config";

/**
 * Super-admin login portal. Only `super_admin` accounts can sign in here.
 */
export default function SuperAdminLogin() {
  const navigate = useNavigate();
  const { superLogin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const res = await superLogin(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Signed in as super-admin");
      navigate("/admin");
    } else {
      setError(res.error);
    }
  };

  return (
    <AuthShell
      title="Super Admin"
      subtitle={`Unrestricted control panel for ${BRAND.app_name}.`}
      footer={null}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="super-login-form">
        <div className="flex items-center gap-2 rounded-xl border border-pink-500/30 bg-pink-500/8 px-3 py-2 text-[11.5px] text-muted-foreground">
          <Crown size={14} className="text-pink-400 shrink-0" />
          Highest-privilege access. Every action here is audited.
        </div>

        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
            data-testid="super-login-email"
            placeholder="you@company.com"
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            data-testid="super-login-password"
            placeholder="••••••••"
            className="w-full rounded-xl bg-muted/40 border border-border px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {error && (
          <div data-testid="super-login-error" className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit" disabled={busy} data-testid="super-login-submit"
          className="btn-primary w-full py-3 text-sm"
        >
          {busy ? "Signing in…" : "Sign in as super-admin"}
        </button>
      </form>
    </AuthShell>
  );
}
